<template>
  <div class="min-h-screen bg-[#FAF8F8]">

    <!-- ================================================================= -->
    <!-- MODE 1 : UTILISATEUR CONNECTÉ -> TABLEAU DE BORD OPÉRATIONNEL     -->
    <!-- ================================================================= -->
    <div v-if="isAuthenticated" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

      <!-- En-tête du Dashboard -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 class="text-2xl sm:text-3xl font-extrabold text-[#0B1B3D] tracking-tight">
            Tableau de bord
          </h1>
          <p class="text-gray-500 text-xs sm:text-sm mt-1">
            Vue d'ensemble de vos projets et activités opérationnelles en temps réel
          </p>
        </div>

        <div class="flex items-center gap-2.5">
          <router-link to="/kanban"
            class="px-4 py-2 bg-white border border-gray-200 hover:border-[#7C3AED] text-[#0B1B3D] font-bold text-xs rounded-xl shadow-2xs transition">
            Ouvrir le Kanban →
          </router-link>
          <router-link to="/projects"
            style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
            class="px-4 py-2 text-white font-bold text-xs rounded-xl shadow-xs hover:opacity-95 transition">
            Tous les projets
          </router-link>
        </div>
      </div>

      <!-- Stats / KPIs -->
      <section class="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-2xl shadow-xs p-5 border border-gray-100 hover:border-[#7C3AED]/20 transition">
          <p class="text-[11px] text-gray-400 uppercase tracking-wider font-bold">Total Projets</p>
          <p class="text-2xl sm:text-3xl font-extrabold text-[#0B1B3D] mt-1">{{ projects.length }}</p>
        </div>

        <div class="bg-white rounded-2xl shadow-xs p-5 border border-gray-100 hover:border-[#F97316]/20 transition">
          <p class="text-[11px] text-gray-400 uppercase tracking-wider font-bold">En cours</p>
          <p class="text-2xl sm:text-3xl font-extrabold text-[#F97316] mt-1">{{ countStatus('En cours') }}</p>
        </div>

        <div class="bg-white rounded-2xl shadow-xs p-5 border border-gray-100 hover:border-[#7C3AED]/20 transition">
          <p class="text-[11px] text-gray-400 uppercase tracking-wider font-bold">En révision</p>
          <p class="text-2xl sm:text-3xl font-extrabold text-[#7C3AED] mt-1">{{ countStatus('En révision') }}</p>
        </div>

        <div class="bg-white rounded-2xl shadow-xs p-5 border border-gray-100 hover:border-emerald-500/20 transition">
          <p class="text-[11px] text-gray-400 uppercase tracking-wider font-bold">Terminés</p>
          <p class="text-2xl sm:text-3xl font-extrabold text-emerald-600 mt-1">{{ countStatus('Terminé') }}</p>
        </div>
      </section>

      <!-- Grille Principale : Projets & Activités -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">

        <!-- Colonne Projets (2/3) -->
        <div class="lg:col-span-2 space-y-6">

          <!-- Formulaire Nouveau Projet -->
          <section class="bg-white rounded-2xl shadow-xs p-6 border border-gray-100">
            <h2 class="text-base font-bold text-[#0B1B3D] mb-4">Créer un nouvel espace projet</h2>
            <form @submit.prevent="addProject" class="flex flex-col gap-3">
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input v-model="newProject.title" placeholder="Nom du projet" required
                  class="p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] outline-none text-xs text-gray-900 transition" />
                <input v-model="newProject.description" placeholder="Description sommaire" required
                  class="p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] outline-none text-xs text-gray-900 transition" />
                <input v-model="newProject.project_type" placeholder="Type (ex: Web, App)" required
                  class="p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] outline-none text-xs text-gray-900 transition" />
              </div>
              <button type="submit" :disabled="isCreating"
                style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
                class="text-white font-bold px-6 py-2.5 rounded-xl shadow-xs hover:opacity-95 transition text-xs sm:self-end cursor-pointer disabled:opacity-50">
                {{ isCreating ? 'Création...' : 'Créer le projet' }}
              </button>
            </form>
          </section>

          <!-- Liste des projets récents -->
          <section class="bg-white rounded-2xl shadow-xs p-6 border border-gray-100">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-base font-bold text-[#0B1B3D]">Vos projets récents</h2>
              <router-link to="/projects" class="text-xs font-bold text-[#7C3AED] hover:underline">
                Voir tout →
              </router-link>
            </div>

            <div v-if="isLoading" class="text-gray-400 text-xs py-8 text-center">
              Chargement des projets...
            </div>

            <div v-else-if="projects.length === 0"
              class="p-8 border-2 border-dashed border-gray-200 rounded-xl text-center text-xs text-gray-400">
              Aucun projet pour le moment. Créez-en un ci-dessus !
            </div>

            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div v-for="project in projects.slice(0, 4)" :key="project.id"
                class="p-4 bg-[#FAF8F8] border border-gray-100 hover:border-[#7C3AED]/30 rounded-2xl transition cursor-pointer group"
                @click="$router.push(`/projects/${project.id}`)">
                <div class="flex items-start justify-between gap-2">
                  <h3 class="font-bold text-[#0B1B3D] text-xs group-hover:text-[#7C3AED] transition truncate">
                    {{ project.title }}
                  </h3>
                  <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-[#7C3AED]">
                    {{ project.project_type || 'Standard' }}
                  </span>
                </div>
                <p class="text-[11px] text-gray-500 mt-1.5 line-clamp-2">{{ project.description }}</p>

                <div class="mt-3 pt-2.5 border-t border-gray-200/60 flex items-center justify-between text-[10px]">
                  <span class="font-bold text-gray-400">{{ project.status }}</span>
                  <span class="text-[#F97316] font-bold">Ouvrir →</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Colonne Activités (1/3) -->
        <section class="bg-white rounded-2xl shadow-xs p-6 border border-gray-100 flex flex-col">
          <h2 class="text-base font-bold text-[#0B1B3D] mb-4">Activités récentes</h2>
          <div v-if="recentActivities.length === 0" class="text-gray-400 text-xs py-10 text-center my-auto">
            Aucune activité récente enregistrée.
          </div>
          <ul v-else class="space-y-4">
            <li v-for="(act, i) in recentActivities" :key="i" class="flex gap-3 items-start">
              <span class="w-2 h-2 rounded-full bg-[#7C3AED] mt-1.5 shrink-0"></span>
              <div>
                <p class="text-xs text-gray-700 font-medium">{{ act.description }}</p>
                <p class="text-[10px] text-gray-400 mt-0.5">{{ act.date }}</p>
              </div>
            </li>
          </ul>
        </section>

      </div>
    </div>

    <!-- ================================================================= -->
    <!-- MODE 2 : VISITEUR NON CONNECTÉ -> GRANDE LANDING PAGE COMPLETE    -->
    <!-- ================================================================= -->
    <div v-else>

      <!-- 1. HERO SECTION -->
      <section
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 lg:pt-16 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div class="lg:col-span-7 space-y-6">

          <div
            class="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-purple-50 text-xs font-bold text-[#7C3AED] border border-purple-100">
            <span class="w-2 h-2 rounded-full bg-[#7C3AED] animate-pulse"></span>
            Orchestration & Opérations • 100% Gratuit
          </div>

          <h1 class="text-4xl sm:text-5xl lg:text-[3.2rem] font-black tracking-tight text-[#0B1B3D] leading-[1.08]">
            Pilotez vos dossiers clients de la signature à la livraison.
          </h1>

          <p class="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
            <strong>GiraafFlow</strong> structure vos opérations après-vente : découpez vos projets en lots, suivez
            l'avancement en Kanban interactif et automatisez vos flux de travail en équipe.
          </p>

          <div class="flex flex-wrap items-center gap-4 pt-2">
            <router-link to="/register"
              style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
              class="text-white font-bold text-sm px-6 py-3.5 rounded-xl shadow-lg hover:opacity-95 transition">
              Démarrer gratuitement →
            </router-link>

            <router-link to="/login"
              class="bg-white text-[#0B1B3D] font-bold text-sm px-6 py-3.5 rounded-xl border border-gray-200 hover:border-[#7C3AED] shadow-2xs transition">
              Se connecter à mon espace
            </router-link>
          </div>

          <div class="flex items-center gap-6 text-xs text-gray-500 pt-2">
            <span class="flex items-center gap-1.5 font-semibold text-gray-700">
              <svg class="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
              Kanban temps réel
            </span>
            <span class="flex items-center gap-1.5 font-semibold text-gray-700">
              <svg class="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
              Planification par lots
            </span>
            <span class="flex items-center gap-1.5 font-semibold text-gray-700">
              <svg class="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
              </svg>
              Aucun abonnement
            </span>
          </div>
        </div>

        <!-- Aperçu Démo Kanban -->
        <div class="lg:col-span-5">
          <div class="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-xl space-y-4">
            <div class="flex items-center justify-between border-b border-gray-100 pb-3">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-rose-400"></span>
                <span class="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                <span class="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                <span class="text-xs font-bold text-gray-700 ml-2">Aperçu Kanban Opérationnel</span>
              </div>
              <span class="text-[10px] font-bold bg-purple-50 text-[#7C3AED] px-2 py-0.5 rounded-md">Live Preview</span>
            </div>

            <div class="space-y-3">
              <div
                class="p-3.5 bg-[#FAF8F8] rounded-2xl border border-gray-100 space-y-1.5 hover:border-[#7C3AED]/30 transition">
                <div class="flex justify-between items-center">
                  <span class="text-xs font-bold text-[#0B1B3D]">Refonte Plateforme Web</span>
                  <span class="text-[10px] bg-orange-100 text-[#F97316] font-bold px-2 py-0.5 rounded-full">En
                    cours</span>
                </div>
                <p class="text-[11px] text-gray-500">Lot 1 : Maquettage UX & Validation client</p>
              </div>

              <div
                class="p-3.5 bg-[#FAF8F8] rounded-2xl border border-gray-100 space-y-1.5 hover:border-[#7C3AED]/30 transition">
                <div class="flex justify-between items-center">
                  <span class="text-xs font-bold text-[#0B1B3D]">Intégration API Stripe</span>
                  <span class="text-[10px] bg-purple-100 text-[#7C3AED] font-bold px-2 py-0.5 rounded-full">En
                    révision</span>
                </div>
                <p class="text-[11px] text-gray-500">Lot 2 : Recette technique & Tests de paiement</p>
              </div>

              <div
                class="p-3.5 bg-[#FAF8F8] rounded-2xl border border-gray-100 space-y-1.5 hover:border-[#7C3AED]/30 transition">
                <div class="flex justify-between items-center">
                  <span class="text-xs font-bold text-[#0B1B3D]">Audit Sécurité & Déploiement</span>
                  <span
                    class="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full">Terminé</span>
                </div>
                <p class="text-[11px] text-gray-500">Lot 3 : Livraison validée par le superviseur</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 2. LES 6 PILIERS FONCTIONNELS -->
      <section id="fonctionnalites" class="bg-white border-t border-b border-gray-200/80 py-20">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center max-w-2xl mx-auto mb-14">
            <span class="text-xs font-bold uppercase tracking-wider text-[#7C3AED]">Fonctionnalités Clés</span>
            <h2 class="text-3xl sm:text-4xl font-extrabold text-[#0B1B3D] tracking-tight mt-2">
              Tout pour orchestrer vos opérations d'équipe
            </h2>
            <p class="text-xs sm:text-sm text-gray-500 mt-2">
              Conçu pour éliminer les goulots d'étranglement entre la signature et la livraison finale.
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="feat in features" :key="feat.title"
              class="p-6 bg-[#FAF8F8] rounded-2xl border border-gray-100 space-y-3 hover:border-[#7C3AED]/30 hover:shadow-xs transition">
              <div class="w-10 h-10 rounded-xl bg-purple-50 text-[#7C3AED] flex items-center justify-center font-bold">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="feat.icon" />
                </svg>
              </div>
              <h3 class="text-base font-bold text-[#0B1B3D]">{{ feat.title }}</h3>
              <p class="text-xs text-gray-600 leading-relaxed">{{ feat.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 3. COMMENT ÇA MARCHE EN 3 ÉTAPES -->
      <section id="fonctionnement" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B1B3D] leading-tight max-w-xl">
          De la mission à la livraison, en 3 étapes.
        </h2>

        <div class="mt-12 grid md:grid-cols-3 gap-8">
          <div v-for="(step, i) in steps" :key="step.title" class="space-y-3">
            <div class="flex items-center gap-3">
              <span style="background: linear-gradient(135deg, #7C3AED 0%, #F97316 100%);"
                class="w-9 h-9 shrink-0 rounded-full text-white text-sm font-bold flex items-center justify-center">
                {{ i + 1 }}
              </span>
              <span v-if="i < steps.length - 1" class="hidden md:block h-px flex-1 bg-gray-200"></span>
            </div>
            <h3 class="text-base font-bold text-[#0B1B3D]">{{ step.title }}</h3>
            <p class="text-xs text-gray-600 leading-relaxed">{{ step.desc }}</p>
          </div>
        </div>
      </section>

      <!-- 4. BANDEAU D'APPEL À L'ACTION FINAL -->
      <section class="px-4 sm:px-6 lg:px-8 pb-20">
        <div style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
          class="max-w-7xl mx-auto rounded-3xl px-8 py-12 sm:px-14 sm:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8 shadow-xl">
          <div>
            <span class="inline-block bg-white/20 text-white font-bold text-xs px-3 py-1 rounded-full mb-3">
              Suite Logicielle Giraaf Park
            </span>
            <h2 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              Prêt à structurer vos flux de travail ?
            </h2>
            <p class="mt-2 text-purple-100 max-w-lg leading-relaxed text-xs sm:text-sm">
              Créez votre premier espace projet gratuitement et faites avancer vos opérations sans friction.
            </p>
          </div>
          <router-link to="/register"
            class="shrink-0 bg-white text-[#0B1B3D] font-bold text-sm px-6 py-3.5 rounded-xl hover:bg-gray-50 transition cursor-pointer shadow-md">
            Créer un compte gratuit →
          </router-link>
        </div>
      </section>

    </div>

  </div>
</template>

<script>
import apiClient from '@/services/apiClient'

const DEFAULT_PROJECTS = [
  { id: 1, title: 'Refonte Plateforme Web', description: 'Découpage UX et intégration front', status: 'En cours', project_type: 'Web' },
  { id: 2, title: 'Intégration API Paiement', description: 'Passerelle bancaire et tests unitaires', status: 'En révision', project_type: 'API' },
  { id: 3, title: 'Audit Sécurité & Déploiement', description: 'Revue de code et mise en production', status: 'Terminé', project_type: 'Infra' }
]

export default {
  name: 'HomeView',
  data() {
    return {
      isAuthenticated: Boolean(localStorage.getItem('token') || localStorage.getItem('giraaf_token')),
      projects: [],
      stats: {},
      recentActivities: [],
      newProject: { title: '', description: '', project_type: '' },
      isLoading: false,
      isCreating: false,
      features: [
        { title: 'Diagrammes & Kanban', desc: 'Visualisation fluide de vos livrables à travers toutes les étapes de production.', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { title: 'Gestion par Lots', desc: 'Découpez chaque mission complexe en sous-lots planifiés avec dates limites.', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
        { title: 'Templates Métiers', desc: 'Modèles pré-configurés pour standardiser vos flux de travail récurrents.', icon: 'M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2' },
        { title: 'Intégrations Écosystème', desc: 'Connexion native avec GiraafChat, GiraafDesk et l\'ensemble des outils Giraaf Park.', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
        { title: 'Analytics & Délais', desc: 'Suivi précis de l\'avancement et anticipation des goulots d\'étranglement.', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
        { title: 'Smart Alerts', desc: 'Alertes instantanées pour ne jamais manquer une livraison ou une validation.', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' }
      ],
      steps: [
        { title: 'Créez un espace projet', desc: "Définissez les objectifs de la mission et configurez les catégories de travail." },
        { title: 'Découpez et planifiez', desc: "Ajoutez des lots précis, attribuez les statuts et suivez l'avancement en Kanban." },
        { title: 'Validez et livrez', desc: "Contrôlez les livrables techniques et passez les projets en statut Terminé." }
      ]
    }
  },
  methods: {
    countStatus(statusName) {
      return this.projects.filter(p => p.status === statusName).length
    },
    async fetchProjects() {
      this.isLoading = true
      try {
        const res = await apiClient.get('/api/v1/projects')
        if (Array.isArray(res.data) && res.data.length > 0) {
          this.projects = res.data
          this.isLoading = false
          return
        }
      } catch (err) {
        console.log('Mode local activé pour les projets')
      }
      this.projects = [...DEFAULT_PROJECTS]
      this.isLoading = false
    },
    async addProject() {
      this.isCreating = true
      const created = {
        id: Date.now(),
        ...this.newProject,
        status: 'Brouillon'
      }
      try {
        await apiClient.post('/api/v1/projects', { project: this.newProject })
      } catch (e) {
        console.log('Création locale')
      }
      this.projects.unshift(created)
      this.newProject = { title: '', description: '', project_type: '' }
      this.isCreating = false
    }
  },
  mounted() {
    if (this.isAuthenticated) {
      this.fetchProjects()
    }
  }
}
</script>
